/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  IconButton,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  MoneyOff as MoneyOffIcon,
  AttachFile as AttachFileIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';
import {
  updatePaymentLinkStatus,
  cancelPaymentLink,
  refundPaymentLink,
  fetchRefundableCharges,
  attachInvoice,
  statusDoLink,
  rotuloDaCobranca,
  bloqueioDoEstorno,
  motivosDeBloqueioDoEstorno,
  motivoDeEstornoIndisponivel,
  podeEstornarLink,
  prazoRestanteDoEstorno,
} from '../paymentLinksActions';

const useStyles = makeStyles((theme) => ({
  leftColumn: {
    paddingRight: theme.spacing(3),
  },
  rightColumn: {
    borderLeft: '1px solid #e0e0e0',
    paddingLeft: theme.spacing(3),
  },
  infoRow: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-start',
  },
  label: {
    fontWeight: 600,
    marginRight: theme.spacing(1),
    color: '#333',
    minWidth: 160,
  },
  value: {
    color: '#666',
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontSize: '1rem',
    color: '#333',
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  moneyValue: {
    fontWeight: 'bold',
    color: '#333',
  },
  smallText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
  },
  statusSelect: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
  actionsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
  },
  financialSection: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
  },
  invoiceSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  attachButton: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffc107',
    '&:hover': {
      backgroundColor: '#ffe69c',
    },
  },
}));

const PaymentLinkDetailModal = ({ open, onClose, link: linkProp, onRefresh, ...props }) => {
  const classes = useStyles();
  const [link, setLink] = useState(linkProp);
  const [hasInvoice, setHasInvoice] = useState(linkProp?.hasInvoice || false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(linkProp?.status || 'pending');
  // Estorno: em link recorrente há uma cobrança por ciclo, cada uma com sua
  // própria data e seu próprio prazo — por isso a escolha é explícita.
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundCharges, setRefundCharges] = useState([]);
  const [refundDays, setRefundDays] = useState(90);
  const [selectedCharge, setSelectedCharge] = useState('');
  const [refundPartial, setRefundPartial] = useState(false);
  const [refundValue, setRefundValue] = useState('');

  // Atualizar link quando a prop mudar
  React.useEffect(() => {
    if (linkProp) {
      setLink(linkProp);
      setCurrentStatus(linkProp.status || 'pending');
      setHasInvoice(linkProp.hasInvoice || false);
    }
  }, [linkProp]);

  if (!link) return null;

  const handleUpdateStatus = async () => {
    setLoading(true);
    try {
      await props.updatePaymentLinkStatus(link.id, currentStatus);
      // Atualizar o link localmente
      setLink({ ...link, status: currentStatus });
      onRefresh();
    } catch (error) {
      // Erro tratado na action
      // Reverter o status se falhar
      setCurrentStatus(link.status);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await props.cancelPaymentLink(link.id);
      onRefresh();
      onClose();
    } catch (error) {
      // Erro tratado na action
    } finally {
      setLoading(false);
    }
  };

  const abrirEstorno = async () => {
    setLoading(true);
    try {
      const dados = await props.fetchRefundableCharges(link.id);
      const cobrancas = dados?.charges || [];
      setRefundCharges(cobrancas);
      setRefundDays(dados?.prazo_dias || 90);
      // Pré-seleciona a mais recente, que é o caso comum.
      const primeiraElegivel = cobrancas.find((c) => c.estornavel);
      setSelectedCharge(
        primeiraElegivel ? String(primeiraElegivel.id ?? 'link') : ''
      );
      setRefundPartial(false);
      setRefundValue('');
      setRefundOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const confirmarEstorno = async () => {
    const escolhida = refundCharges.find(
      (c) => String(c.id ?? 'link') === String(selectedCharge)
    );
    if (!escolhida) return;

    setLoading(true);
    try {
      await props.refundPaymentLink(
        link.id,
        refundPartial,
        refundPartial ? Number(refundValue) : null,
        escolhida.id
      );
      setRefundOpen(false);
      onRefresh();
      onClose();
    } catch (error) {
      // Erro tratado na action
    } finally {
      setLoading(false);
    }
  };

  // Por que o painel não estorna cada cobrança e, quando nenhuma é
  // selecionável, por que "Confirmar estorno" nunca habilita. O backend já
  // devolve isso em motivo_bloqueio; antes a tela jogava tudo em "Expirado".
  const motivosBloqueio = motivosDeBloqueioDoEstorno(refundCharges, refundDays);
  const estornoIndisponivel = motivoDeEstornoIndisponivel(refundCharges, refundDays);

  // O botão seguia a regra da tela ('paid' e nada mais); agora segue a do
  // backend, que aceita a cobrança do link pago (inclusive 'overpaid') e
  // qualquer ciclo ainda pago de um recorrente — é assim que um link
  // 'partial_canceled' volta a aceitar o estorno do ciclo seguinte.
  const estornoHabilitado = podeEstornarLink(link);

  const brl = (valor) =>
    `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`;
  const dataBR = (iso) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

  const handleInvoiceUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setInvoiceFile(file);
      setLoading(true);
      try {
        await props.attachInvoice(link.id, file);
        setHasInvoice(true);
        onRefresh();
      } finally {
        setLoading(false);
      }
    }
  };

  // Rótulos e cores vêm do mesmo mapa da lista: os dois recortes que ficavam
  // aqui não cobriam o ENUM inteiro e mostravam o termo cru em inglês
  // ('chargedback', 'overpaid') justamente nos status que a v5 passou a gravar.
  const getStatusLabel = (status) => statusDoLink(status).label;

  const getChargeStatusLabel = (status) => rotuloDaCobranca(status);

  const formatCurrency = (value) => {
    return `R$ ${parseFloat(value || 0)
      .toFixed(2)
      .replace('.', ',')}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getPaymentMethodsText = () => {
    // Se o status for pago, mostrar o método usado
    if (link.status === 'paid' || link.status === 'refunded') {
      // Se foi atualização manual
      if (link.paymentMethod === 'manual') {
        return `Manual - ${link.manualUpdateByName || 'Usuário'}`;
      }
      // Se foi pagamento real
      if (link.paymentMethod) {
        const labels = {
          credit_card: 'Cartão de crédito',
          cartao: 'Cartão de crédito',
          boleto: 'Boleto',
          pix: 'PIX',
        };
        return labels[link.paymentMethod.toLowerCase()] || link.paymentMethod;
      }
    }
    // Se ainda não foi pago, mostrar métodos disponíveis
    const methods = link.payment_methods || [];
    const labels = {
      credit_card: 'Cartão de crédito',
      cartao: 'Cartão de crédito',
      boleto: 'Boleto',
      pix: 'PIX',
    };
    return methods.map((m) => labels[m] || m).join(', ') || 'Não definido';
  };

  // Cálculos financeiros: considerar taxa de administração e antifraude
  const grossValue = link.value || 0;
  const feePercentage = link.feePercentage || (link.fee && grossValue ? (link.fee / grossValue) * 100 : 0);
  const adminFee = typeof link.fee === 'number' ? link.fee : grossValue * (feePercentage / 100);
  const paymentMethodKey = (link.paymentMethod || '').toLowerCase();
  const antifraudFee = paymentMethodKey === 'credit_card' || paymentMethodKey === 'cartao' ? 1.5 : 0;
  const totalFees = adminFee + antifraudFee;
  const netAfterFees = Math.max(0, grossValue - totalFees);
  const partnerPercentage = link.partnerPercentage || 0;
  const stationPercentage = link.stationPercentage || (100 - partnerPercentage) || 100;
  const partnerNetValue = link.hasPartner ? netAfterFees * (partnerPercentage / 100) : 0;
  const stationNetValue = netAfterFees - partnerNetValue;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Detalhes do Link de Pagamento</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container>
          {/* Coluna Esquerda - Informações */}
          <Grid item xs={12} md={7} className={classes.leftColumn}>
            {/* Dados do Link */}
            <div className={classes.infoRow}>
              <Typography className={classes.label}>ID:</Typography>
              <Typography className={classes.value}>#{link.id}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>ID Pagar.me:</Typography>
              <Typography className={classes.value}>
                #{link.transactionId || 'Aguardando'}
              </Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Data de emissão:</Typography>
              <Typography className={classes.value}>{formatDate(link.createdAt)}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Data de vencimento:</Typography>
              <Typography className={classes.value}>{formatDate(link.expiresAt)}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Status do pedido:</Typography>
              <Typography
                className={classes.value}
                style={{
                  color: statusDoLink(link.status).style.color,
                  fontWeight: 'bold',
                }}>
                {getStatusLabel(link.status)}
              </Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Nome:</Typography>
              <Typography className={classes.value}>{link.name}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>CPF:</Typography>
              <Typography className={classes.value}>{link.cpf}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Telefone:</Typography>
              <Typography className={classes.value}>{link.phone || 'Não informado'}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>E-mail:</Typography>
              <Typography className={classes.value}>{link.email || 'Não informado'}</Typography>
            </div>
            {(link.partner || link.hasPartner) && (
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Parceiro:</Typography>
                <Typography className={classes.value}>
                  {link.partner?.name || 'Parceiro não identificado'}
                </Typography>
              </div>
            )}
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Atividade/Serviço:</Typography>
              <Typography className={classes.value}>
                {link.activity || 'Link Individual'}
              </Typography>
            </div>
            {link.saleType && (
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Tipo de Venda:</Typography>
                <Typography className={classes.value}>{link.saleType}</Typography>
              </div>
            )}

            {/* Tabela de Produtos */}
            <Typography className={classes.sectionTitle}>Produtos</Typography>
            <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
              <Table size="small">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Quantidade</TableCell>
                    <TableCell align="right">Valor (R$)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{link.activity || link.service || 'Link de Pagamento'}</TableCell>
                    <TableCell align="center">1</TableCell>
                    <TableCell align="right">{formatCurrency(link.value)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Informações Financeiras */}
            <Box className={classes.financialSection}>
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Valor destinado ao parceiro:</Typography>
                <Typography className={classes.value}>
                  {formatCurrency(partnerNetValue || 0)} ({partnerPercentage || 0}%)
                </Typography>
              </div>
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Valor destinado à entidade:</Typography>
                <Typography className={classes.value}>
                  {formatCurrency(stationNetValue || 0)} ({stationPercentage || 100}%)
                </Typography>
              </div>
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Forma de Pagamento:</Typography>
                <Typography className={classes.value}>{getPaymentMethodsText()}</Typography>
              </div>
              <div className={classes.infoRow}>
                <Typography className={classes.label}>Parcelamento:</Typography>
                <Typography className={classes.value}>
                  {link.allowInstallments ? 'Até 12x' : '1x'} {formatCurrency(link.value)}
                </Typography>
              </div>
            </Box>

            <Divider className={classes.divider} />

            {/* Valores Pagos */}
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Valor pago do cartão/PIX:</Typography>
              <Typography className={classes.moneyValue}>{formatCurrency(grossValue)}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography className={classes.label}>Valor líquido recebido:</Typography>
              <Typography className={classes.moneyValue}>
                {formatCurrency(netAfterFees)}*
              </Typography>
            </div>
            <Typography className={classes.smallText}>
              *Os valores exibidos já consideram a taxa de administração aplicada à transação e,
              para compras no cartão de crédito, o acréscimo de R$ 1,50 de antifraude por
              transação.
            </Typography>

            {Array.isArray(link.charges) && link.charges.length > 0 && (
              <>
                <Typography className={classes.sectionTitle}>
                  Histórico de cobranças
                  {link.recurrence_type && link.recurrence_type !== 'single'
                    ? ` (${link.paidChargesCount || 0} paga(s))`
                    : ''}
                </Typography>
                <TableContainer
                  component={Paper}
                  className={classes.tableContainer}
                  elevation={0}>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell align="center">#</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {link.charges.map((charge) => (
                        <TableRow key={`${charge.chargeNumber}-${charge.transactionId || ''}`}>
                          <TableCell align="center">{charge.chargeNumber}</TableCell>
                          <TableCell>{formatDate(charge.paidAt || charge.createdAt)}</TableCell>
                          <TableCell>{getChargeStatusLabel(charge.status)}</TableCell>
                          <TableCell align="right">{formatCurrency(charge.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Grid>

          {/* Coluna Direita - Ações */}
          <Grid item xs={12} md={5} className={classes.rightColumn}>
            {/* Nota Fiscal */}
            <Box className={classes.invoiceSection}>
              <Typography>Há Nota Fiscal?</Typography>
              <Typography>Não</Typography>
              <Switch
                checked={hasInvoice}
                onChange={(e) => setHasInvoice(e.target.checked)}
                color="primary"
              />
              <Typography>Sim</Typography>
              {hasInvoice && (
                <>
                  <input
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    id="invoice-upload"
                    type="file"
                    onChange={handleInvoiceUpload}
                  />
                  <label htmlFor="invoice-upload">
                    <Button variant="outlined" component="span" size="small">
                      ANEXAR DOCUMENTO
                    </Button>
                  </label>
                </>
              )}
            </Box>
            <Button
              variant="outlined"
              className={classes.attachButton}
              fullWidth
              style={{ marginBottom: 16 }}>
              Anexar nota fiscal
            </Button>

            {/* Status */}
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Status
            </Typography>
            <FormControl variant="outlined" fullWidth className={classes.statusSelect}>
              <Select value={currentStatus} onChange={(e) => setCurrentStatus(e.target.value)}>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="paid">Pago</MenuItem>
                <MenuItem value="expired">Expirado</MenuItem>
                <MenuItem value="canceled">Cancelado</MenuItem>
              </Select>
            </FormControl>

            {/* Botões de Ação */}
            <Box className={classes.actionsContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateStatus}
                disabled={loading}
                fullWidth
                className={classes.actionButton}>
                ATUALIZAR STATUS
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: '#dc3545', color: 'white' }}
                onClick={handleCancel}
                disabled={loading || link.status === 'canceled'}
                fullWidth
                className={classes.actionButton}>
                CANCELAR Link
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={abrirEstorno}
                disabled={loading || !estornoHabilitado}
                fullWidth
                className={classes.actionButton}>
                ESTORNAR
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>

      {/* Seleção da cobrança a estornar. Um link recorrente acumula uma
          cobrança por ciclo, e o prazo de estorno corre a partir do pagamento
          de cada uma — não do link. */}
      <Dialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Estornar cobrança</DialogTitle>
        <DialogContent>
          {refundCharges.length === 0 ? (
            <Typography variant="body2" style={{ padding: '16px 0' }}>
              {estornoIndisponivel}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                O estorno automático vale por {refundDays} dias após o pagamento.
                Cobranças fora do prazo precisam ser tratadas pelo suporte da
                Pagar.me.
              </Typography>

              <TableContainer component={Paper} variant="outlined" style={{ marginTop: 12 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Cobrança</TableCell>
                      <TableCell>Paga em</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">Prazo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refundCharges.map((c) => {
                      const chave = String(c.id ?? 'link');
                      const bloqueio = bloqueioDoEstorno(c, refundDays);
                      return (
                        <TableRow
                          key={chave}
                          hover
                          selected={selectedCharge === chave}
                          onClick={() => c.estornavel && setSelectedCharge(chave)}
                          style={{
                            cursor: c.estornavel ? 'pointer' : 'not-allowed',
                            opacity: c.estornavel ? 1 : 0.5,
                          }}>
                          <TableCell padding="checkbox">
                            <input
                              type="radio"
                              name="cobranca-estorno"
                              checked={selectedCharge === chave}
                              disabled={!c.estornavel}
                              onChange={() => setSelectedCharge(chave)}
                            />
                          </TableCell>
                          <TableCell>
                            {c.ciclo ? `${c.ciclo}º ciclo` : 'Cobrança única'}
                          </TableCell>
                          <TableCell>{dataBR(c.paid_at)}</TableCell>
                          <TableCell align="right">{brl(c.amount)}</TableCell>
                          <TableCell align="right" title={bloqueio ? bloqueio.detalhe : ''}>
                            {bloqueio
                              ? bloqueio.curto
                              : prazoRestanteDoEstorno(c, refundDays)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {motivosBloqueio.length > 0 && (
                <Box
                  mt={1.5}
                  p={1.5}
                  style={{ backgroundColor: '#fff8e1', color: '#8d6e00', borderRadius: 4 }}>
                  {estornoIndisponivel ? (
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {estornoIndisponivel}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        Cobranças que o painel não estorna:
                      </Typography>
                      {motivosBloqueio.map((motivo) => (
                        <Typography key={motivo} variant="body2">
                          {motivo}
                        </Typography>
                      ))}
                    </>
                  )}
                </Box>
              )}

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Switch
                    checked={refundPartial}
                    onChange={(e) => setRefundPartial(e.target.checked)}
                    color="primary"
                  />
                }
                label="Estornar apenas parte do valor"
              />

              {refundPartial && (
                <TextField
                  fullWidth
                  type="number"
                  label="Valor a estornar"
                  value={refundValue}
                  onChange={(e) => setRefundValue(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  helperText="Não pode ser maior que o valor da cobrança selecionada."
                  margin="normal"
                />
              )}

              <Box display="flex" justifyContent="flex-end" gridGap={8} mt={2}>
                <Button onClick={() => setRefundOpen(false)} disabled={loading}>
                  Voltar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={confirmarEstorno}
                  disabled={
                    loading ||
                    !selectedCharge ||
                    (refundPartial && !(Number(refundValue) > 0))
                  }>
                  Confirmar estorno
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updatePaymentLinkStatus,
      cancelPaymentLink,
      refundPaymentLink,
      fetchRefundableCharges,
      attachInvoice,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(PaymentLinkDetailModal);
